//? Note: jurisdiction specific component
//TODO: refactor

import React, { useState, useEffect } from 'react';
import Cleave from 'cleave.js/react';
import * as yup from 'yup';
import states from './states.json';
import { connect } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../Store';
import {
    ContentBox,
    ContentBoxHead,
    ContentBoxBody,
    CMSContent,
    StateSelectionList,
    ScrollToTop,
    getCMSObject,
    TeleScript,
    DisplayContent,
    AlertBox,
    ErrorMessage,
} from 'sg-ui-components';
import { Redirect } from 'react-router-dom';

import promotionConfig from '../../promotionConfig';

/**********************************************************************
 * Component:  StepThreeTemplate
 * Purpose:    Allows users to enter in Address and phone informstion
 *             Combines information in step2Template here to update the
 *             player with an updateRegister call
 *
 * Props:       step -  counter of what step we are in the registration process
 *              setStep -  a method to change steps in the process
 *              verifyFields -  fields that we will verify upon completion of form
 *              setVerifyFiels - method to set the verification fields
 *              user -  user data store
 *              actions - store actions (apis
 *
 * APIs used:   userActions.update - updates player information
 *
 * Notes:
 */
const StepThreeTemplate = ({ step, setStep, verifyFields, setVerifyFields, user, cmsSourceFirebase, actions }) => {
    const telescript = getCMSObject('data.components.teleScripts.registerForm.jsonBlock');
    const isVerifyRequired = user?.player?.actions?.includes('verify-account');

    const initialStepFields = {
        state: user.player.address ? user.player.address.state : promotionConfig.jurisdiction,
        address1: user.player.address ? user.player.address.address1 : '',
        address2: user.player.address ? user.player.address.address2 : '',
        city: user.player.address ? user.player.address.city : '',
        zip: user.player.address ? user.player.address.zip : '',
        phone: user.player.phone ?? '',
    };

    const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

    //***********************************************************************************
    // Schemas for field verification
    const schema = yup.object().shape({
        address1: yup.string().required('Address Line 1 is required').max(200, 'Address Line 1 cannot exceed 200 characters'),

        address2: yup.string().max(200, 'Address Line 2 cannot exceed 200 characters').nullable(),

        city: yup.string().required('City is required').max(30, 'City cannot exceed 30 characters'),

        state: yup
            .string()
            .required('State is required')
            .min(2, 'State must be 2 characters')
            .max(2, 'State must be 2 characters')
            .oneOf(
                states.map((st) => {
                    return st.abbr;
                }),
                'Must be a valid state'
            ),

        zip: yup.string().min(5).max(5).required('Zip is required'),

        phone: yup.string().matches(phoneRegExp, 'Phone number is not valid').required('Phone is required'),
    });

    const [stepFields, setStepFields] = useState(initialStepFields);
    const [localErrors, setLocalErrors] = useState({});
    const [loaded, setLoaded] = useState(false);

    /* Since the user can jump into registration at different points in the process (if they were kicked out previously)
       so we need to make sure that email address is in place/valid before we submit - if validateFields doesn't have it
       grab it from the user store.
     */
    useEffect(() => {
        if (user.player && verifyFields.email.length <= 0 && user.player.email && user.player.email.length > 0) {
            setVerifyFields({
                ...verifyFields,
                email: user.player.email,
            });
        } else {
            setLoaded(true);
        }
    }, [verifyFields, user]);

    //***********************************************************************************
    // if the form field changes, we will change the value in the store and
    const handleChange = (event) => {
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value.trimStart();
        const name = target.name;

        setStepFields({
            ...stepFields,
            [name]: value,
        });
    };

    //***********************************************************************************
    // Validate all fields and report errors if they fail schemas.
    const stepSubmit = async (e) => {
        e.preventDefault();

        setLocalErrors({});
        await actions.userActions.updateSection({ section: 'registration-step-3', status: 'initial' });

        let valid = await schema.isValid(stepFields);

        if (valid) {
            let fieldsToPush = {
                ...verifyFields,
                address: {
                    state: stepFields.state ?? '',
                    address1: stepFields.address1 ?? '',
                    address2: stepFields.address2 ?? '',
                    city: stepFields.city ?? '',
                    zip: stepFields.zip ?? '',
                },
                phone: stepFields.phone ?? '',
            };

            await setVerifyFields(fieldsToPush);
            await actions.userActions.update(fieldsToPush);
            await actions.userActions.updateSection({ section: 'registration-step-3', status: 'submitted' });
        } else {
            await schema.validate(stepFields, { abortEarly: false }).catch(function (err) {
                setLocalErrors(getValidationErrors(err));
            });
        }

        if (document.getElementById('alert')) {
            document.getElementById('alert').scrollIntoView(true);
        }
    };

    const getValidationErrors = (err) => {
        const errors = err.inner || [];

        const validationErrors = errors.reduce((acc, error) => {
            if (error.path) {
                return { ...acc, [error.path]: error.message };
            }
            return acc;
        }, {});

        return validationErrors;
    };

    if (user.updateSuccess && !isVerifyRequired) {
        return <Redirect to='/await-account-confirmation' />;
    }

    if (user.updateSuccess) {
        if (promotionConfig.needSSNToRegister) {
            setStep({ step: 4 });
        } else {
            return <Redirect to='/await-account-confirmation' />;
        }
    } else if (step.step == 3 && loaded) {
        return (
            <div className='row justify-content-center'>
                <ScrollToTop />
                <div id='account-information' className='col-12 col-md-10 col-lg-8'>
                    <ContentBox variant='theme-primary'>
                        <ContentBoxHead>
                            <CMSContent
                                localStorageObject='webContent'
                                contentPath='data.registrationItems.registrationStepThree.contentHeaderText'
                                cmsSourceFirebase={cmsSourceFirebase}
                            />
                        </ContentBoxHead>
                        <ContentBoxBody>
                            <div className='form-step'>
                                <div className='inner-step'>
                                    <div className='col'>
                                        <CMSContent
                                            localStorageObject='webContent'
                                            contentPath='data.registrationItems.registrationStepThree.contentHTML'
                                            cmsSourceFirebase={cmsSourceFirebase}
                                        />
                                    </div>

                                    <DisplayContent
                                        note='Display errors when api call fails. Error code will be transformed to a player friendly message.'
                                        isVisible={user?.updateSection?.section === 'registration-step-3' && user?.updateSection?.status === 'submitted'}>
                                        <ErrorMessage code={user?.errors} collection='data.messages.errorMessages.jsonBlock' />
                                    </DisplayContent>

                                    <DisplayContent
                                        note='Display local errors when validation fails.'
                                        isVisible={localErrors && Object.keys(localErrors)?.length}>
                                        <AlertBox>
                                            <ul className='m-0'>
                                                {Object.values(localErrors).map((err, index) => {
                                                    return <li key={index}>{err}</li>;
                                                })}
                                            </ul>
                                        </AlertBox>
                                    </DisplayContent>

                                    <div className='form-row'>
                                        <div className='form-group col'>
                                            <label htmlFor='address1'>Address</label>
                                            <input
                                                type='email'
                                                className='form-control theme-input'
                                                id='address1'
                                                placeholder='Address'
                                                value={stepFields.address1}
                                                name='address1'
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className='form-row'>
                                        <div className='form-group col'>
                                            <label htmlFor='address2'>Address Line 2 (optional)</label>
                                            <input
                                                type='text'
                                                className='form-control theme-input'
                                                id='address2'
                                                placeholder='Address Line 2'
                                                value={stepFields.address2}
                                                name='address2'
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className='form-row'>
                                        <div className='form-group col'>
                                            <label htmlFor='city'>City</label>
                                            <input
                                                type='text'
                                                className='form-control theme-input'
                                                id='city'
                                                placeholder='City'
                                                value={stepFields.city}
                                                name='city'
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className='form-group col'>
                                            <label htmlFor='state'>State</label>
                                            <StateSelectionList currentVal={stepFields.state} changeCB={handleChange} />
                                        </div>
                                        <div className='form-group col'>
                                            <label htmlFor='zip'>Zip</label>
                                            <Cleave
                                                type='text'
                                                className='form-control theme-input'
                                                id='zip'
                                                placeholder='Zip Code'
                                                value={stepFields.zip}
                                                name='zip'
                                                options={{
                                                    blocks: [5],
                                                    numericOnly: true,
                                                }}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className='form-row'>
                                        <div className='form-group col'>
                                            <label htmlFor='phone'>Phone Number</label>
                                            <Cleave
                                                className='form-control theme-input col-6'
                                                id='phone'
                                                placeholder='###-###-####'
                                                value={stepFields.phone}
                                                name='phone'
                                                options={{
                                                    blocks: [3, 3, 4],
                                                    delimiter: '-',
                                                    numericOnly: true,
                                                }}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <small>You may return to Step 2 if you need to make any changes before submitting.</small>
                                </div>
                            </div>

                            <div className='bottom-section text-center mb-3'>
                                <button type='button' className='btn theme-btn theme-primary' onClick={stepSubmit}>
                                    {isVerifyRequired ? (
                                        <TeleScript line={telescript?.continueStep3Text}>Continue</TeleScript>
                                    ) : (
                                        <TeleScript line={telescript?.submitStep3Text}>Submit</TeleScript>
                                    )}
                                </button>
                            </div>
                        </ContentBoxBody>
                    </ContentBox>
                </div>
            </div>
        );
    } else {
        return null;
    }
};

const StepThree = connect(mapStateToProps, mapDispatchToProps)(StepThreeTemplate);

export { StepThree };
